import { useEffect, useState } from 'react';
import {
  Badge, Button, Group, Loader, Modal, Stack, Table, Text, TextInput, Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { TeamDraft } from '../types/team-draft.type';

const draftClient = new HttpClient<TeamDraft>('/team-draft');

function TeamDraftList() {
  const [drafts, setDrafts] = useState<TeamDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSeason, setNewSeason] = useState('2026/2027');
  const [newName, setNewName] = useState('');
  const [opened, { open, close }] = useDisclosure(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await draftClient.getAll();
      setDrafts(data);
    } catch {
      notifications.show({ color: 'red', message: 'Entwürfe konnten nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newSeason.trim()) return;
    setCreating(true);
    try {
      await draftClient.post({ season: newSeason.trim(), name: newName.trim() || undefined } as any);
      setNewSeason('2026/2027');
      setNewName('');
      close();
      load();
    } catch {
      notifications.show({ color: 'red', message: 'Erstellen fehlgeschlagen.' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    try {
      await draftClient.delete(id);
      setDrafts(prev => prev.filter(d => d.id !== id));
    } catch {
      notifications.show({ color: 'red', message: 'Löschen fehlgeschlagen.' });
    }
  };

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={2}>Team-Entwürfe</Title>
        <Button onClick={open}>Neuer Entwurf</Button>
      </Group>

      <Modal opened={opened} onClose={close} title="Neuer Entwurf" withinPortal={false}>
        <Stack gap="sm">
          <TextInput
            label="Saison"
            value={newSeason}
            onChange={e => setNewSeason(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Name (optional)"
            value={newName}
            onChange={e => setNewName(e.currentTarget.value)}
            placeholder="z. B. Vorschlag A"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>Abbrechen</Button>
            <Button loading={creating} onClick={handleCreate}>Erstellen</Button>
          </Group>
        </Stack>
      </Modal>

      {loading ? (
        <Loader />
      ) : drafts.length === 0 ? (
        <Text c="dimmed">Noch keine Entwürfe vorhanden.</Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Saison</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Spieler</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {drafts.map(draft => (
              <Table.Tr
                key={draft.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/drafts/${draft.id}`)}
              >
                <Table.Td>{draft.season}</Table.Td>
                <Table.Td>{draft.name ?? '—'}</Table.Td>
                <Table.Td>
                  <Badge color={draft.status === 'finalized' ? 'green' : 'blue'}>
                    {draft.status === 'finalized' ? 'Abgeschlossen' : 'Entwurf'}
                  </Badge>
                </Table.Td>
                <Table.Td>{draft._playerCount ?? '—'}</Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    color="red"
                    variant="subtle"
                    onClick={e => handleDelete(e, draft.id)}
                  >
                    Löschen
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </>
  );
}

export default TeamDraftList;
